import { HttpInterceptorFn, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStateService } from '../state/auth-state.service';
import { AUTH_ERROR_SESSION_EXPIRED, AUTH_SESSION_END_EXPIRED, BASE_API_URL } from '@dental-ui/shared/consts';
import { switchMap, throwError, catchError } from 'rxjs';

/** Auth gerektirmeyen API path'leri - bu isteklerde token kontrolü yapılmaz */
const PUBLIC_AUTH_PATHS = [
  'api/Auth/Login',
  'api/Auth/CheckActiveSession',
  'api/Auth/register',
  'api/Auth/RefreshToken',
  'api/Auth/ForgetPassword',
  'api/Auth/UserActivation',
  'api/Auth/ResendActivationEmail',
  'api/Auth/Me',
  'api/Branch/BranchInvetedAcceptUser',
  'api/Share/GetByLink',
  'api/Attachment/GetAttachmentByIsActiveAndType',
  'api/Attachment/GetAttachmentBIsActiveAndViewerJs',
  'api/AttachmentAzure/GetByFileName',
  'api/Attachment/AddAttachmentWithQr',
  // Register (unauthenticated) workspace setup dropdowns
  'api/Country/GetAll',
  'api/State/GetAllStateByCountryId',
];

function isPublicAuthEndpoint(url: string): boolean {
  const path = url.split('?')[0];
  return PUBLIC_AUTH_PATHS.some((p) => path.includes(p));
}

function isApiRequest(req: HttpRequest<unknown>): boolean {
  const base = BASE_API_URL.endsWith('/') ? BASE_API_URL.slice(0, -1) : BASE_API_URL;
  return req.url.startsWith(base);
}

/**
 * Her API isteğinde token süresini kontrol eder.
 * Token süresi dolmuşsa ve refresh token geçerliyse /Auth/RefreshToken ile access token yeniler.
 * Token geçerli görünüyor ama sunucudan 401 alınırsa, yenilemeyi zorlar ve isteği tekrarlar.
 * Her iki token da dolmuşsa logout yapar.
 */
export const authTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req) || isPublicAuthEndpoint(req.url)) {
    return next(req);
  }

  const authState = inject(AuthStateService);

  return authState.ensureValidToken().pipe(
    switchMap((token) => {
      if (!token) {
        // ForceLogout dialog beklenirken login'e zorlama / yanıltıcı SessionExpired log yok
        if (authState.isForceLogoutUiPending()) {
          console.warn(
            '[Auth][Aracı] Token yok — ForceLogout dialog bekleniyor, istek iptal',
            req.method,
            req.url
          );
          return throwError(() => new Error(AUTH_ERROR_SESSION_EXPIRED));
        }
        // handleExpiredAndLogout zaten çağrıldıysa oturum kapalıdır; soft-fail'de yanıltıcı session-end yazma
        if (!authState.isAuthenticated() || authState.isRefreshTokenExpired()) {
          authState.reportSessionEnd(
            AUTH_SESSION_END_EXPIRED,
            `aracı: token yenileme boş döndü — ${req.method} ${req.url}`
          );
        } else {
          console.warn(
            '[Auth][Aracı] Token yenileme geçici başarısız — istek iptal, oturum açık',
            req.method,
            req.url
          );
        }
        return throwError(() => new Error(AUTH_ERROR_SESSION_EXPIRED));
      }
      const cloned = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
      return next(cloned).pipe(
        catchError((error) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            if (authState.isForceLogoutUiPending()) {
              console.warn(
                '[Auth][Aracı] API 401 — ForceLogout dialog bekleniyor, yenileme yok',
                req.method,
                req.url
              );
              return throwError(() => error);
            }
            // 401: refresh dene (refresh dolmuşsa forceRefreshToken logout + login yönlendirir)
            console.warn('[Auth][Aracı] API 401 — zorla yenileme deneniyor', req.method, req.url);
            return authState.forceRefreshToken().pipe(
              switchMap((newToken) => {
                if (!newToken) {
                  if (authState.isForceLogoutUiPending()) {
                    return throwError(() => error);
                  }
                  if (!authState.isAuthenticated() || authState.isRefreshTokenExpired()) {
                    authState.reportSessionEnd(
                      AUTH_SESSION_END_EXPIRED,
                      `aracı: 401 sonrası yenileme başarısız — ${req.method} ${req.url}`
                    );
                  } else {
                    console.warn(
                      '[Auth][Aracı] 401 sonrası yenileme geçici başarısız — oturum açık, istek tekrarlanmadı',
                      req.method,
                      req.url
                    );
                  }
                  return throwError(() => error);
                }
                console.info(
                  '[Auth][Aracı] Yenileme başarılı — istek tekrarlanıyor',
                  req.method,
                  req.url
                );
                const newCloned = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                });
                return next(newCloned);
              })
            );
          }
          return throwError(() => error);
        })
      );
    })
  );
};
